import { useCallback, useEffect, useState } from 'react';
import type { Loan, LoanEvent } from '@/types';
import { listEvents } from '@/lib/db';
import { TimelineItem } from './components/TimelineItem';
import { EventSheet } from '../events/components/EventSheet';
import { EventDetailSheet } from './components/EventDetailSheet';
import { TimelineIcon } from '@/components/icons';

interface TimelinePageProps {
  loan: Loan;
}

type SheetState =
  | { mode: 'closed' }
  | { mode: 'add' }
  | { mode: 'detail'; event: LoanEvent }
  | { mode: 'edit'; event: LoanEvent };

export function TimelinePage({ loan }: TimelinePageProps) {
  const [events, setEvents] = useState<LoanEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sheet, setSheet] = useState<SheetState>({ mode: 'closed' });

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listEvents({ loanId: loan.id });
      setEvents(result);
    } finally {
      setIsLoading(false);
    }
  }, [loan.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="stack">
      <div>
        <h1>Timeline</h1>
        <p>Every disbursement, rate change, prepayment, and correction — in one place, newest first.</p>
      </div>

      {isLoading && <p className="text-muted">Loading…</p>}

      {!isLoading && events.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <TimelineIcon size={28} />
          </div>
          <p>No events yet. Tap the + button to record your first disbursement, rate change, or prepayment.</p>
        </div>
      )}

      {!isLoading && events.length > 0 && (
        <div className="timeline-list">
          {events.map((event) => (
            <TimelineItem key={event.id} event={event} onClick={() => setSheet({ mode: 'detail', event })} />
          ))}
        </div>
      )}

      <button className="fab" onClick={() => setSheet({ mode: 'add' })} aria-label="Add Event">
        +
      </button>

      {sheet.mode === 'add' && (
        <EventSheet loan={loan} events={events} onClose={() => setSheet({ mode: 'closed' })} onSaved={refresh} />
      )}

      {sheet.mode === 'detail' && (
        <EventDetailSheet
          event={sheet.event}
          onClose={() => setSheet({ mode: 'closed' })}
          onEdit={() => setSheet({ mode: 'edit', event: sheet.event })}
          onDeleted={refresh}
        />
      )}

      {sheet.mode === 'edit' && (
        <EventSheet
          loan={loan}
          events={events}
          editingEvent={sheet.event}
          onClose={() => setSheet({ mode: 'closed' })}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
